import React, {
  createContext, useContext,
  useEffect, useCallback, useRef,
  useSyncExternalStore
} from "react";
import { useAuth } from "./AuthContext";
import {
  LANGUAGES, VOICE_CODES,
  RTL_LANGS, AI_INSTRUCTION,
  getFallback, getBaseCode,
  isValidLang, normalizeLang,
  getLanguage, setLanguage as setStoreLanguage,
  subscribeLanguage, writeStoredLanguage
} from "../i18n/languages";
import {
  initUiStrings, getString,
  subscribeUiStrings, getUiStringsVersion
} from "../i18n/uiStrings";
import { db } from "../firebase/config";
import {
  doc, setDoc
} from "firebase/firestore";

// ─── Context ──────────────────────────────────────
const LanguageContext = createContext(null);

// NOTE ON THE STORE
// The selected language lives in src/i18n/languages.js, NOT in this file's
// useState. That module is the single authoritative store; this provider and
// AppContext are both views over it (via useSyncExternalStore).
//
// Why: AppProvider is mounted OUTSIDE LanguageProvider in App.js, so it can
// never read this context. Before the unification each kept its own copy in
// its own localStorage key, and picking Tamil in the navbar left AppContext's
// 251 `t(hi, en)` calls and 877 `hi ? … : …` ternaries frozen in Hindi.
// One store outside React fixes it in both directions without reordering the
// provider tree.

// ─── Apply font for script ────────────────────────
function applyFont(code) {
  // For mixed modes, use base code for font
  const base = getBaseCode(code);

  const FONT_MAP = {
    hi:"Noto Sans Devanagari",
    mr:"Noto Sans Devanagari",
    ne:"Noto Sans Devanagari",
    bn:"Noto Sans Bengali",
    as:"Noto Sans Bengali",
    mni:"Noto Sans Bengali",
    gu:"Noto Sans Gujarati",
    pa:"Noto Sans Gurmukhi",
    ta:"Noto Sans Tamil",
    te:"Noto Sans Telugu",
    kn:"Noto Sans Kannada",
    ml:"Noto Sans Malayalam",
    or:"Noto Sans Oriya",
    ur:"Noto Nastaliq Urdu",
    ks:"Noto Nastaliq Urdu",
    en:"Roboto"
  };

  const font = FONT_MAP[base] || "Roboto";
  document.documentElement.style
    .setProperty(
      "--app-font",
      `'${font}', sans-serif`
    );

  // Load font if not loaded
  const fontId = `font-${base}`;
  if (
    !document.getElementById(fontId) &&
    font !== "Roboto"
  ) {
    const link = document.createElement("link");
    link.id   = fontId;
    link.rel  = "stylesheet";
    link.href =
      `https://fonts.googleapis.com/css2?` +
      `family=${encodeURIComponent(font)}` +
      `:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }
}

// ─── Provider ─────────────────────────────────────
export function LanguageProvider({ children }) {
  const { currentUser, userProfile } = useAuth();

  // Language comes from the shared store. Resolution order on a cold start,
  // handled inside languages.js:
  //   1. localStorage 'fassalkart_lang'
  //   2. legacy 'fassalkartai_lang' / 'kisanai_lang' (migrated, then deleted)
  //   3. 'en'
  // The signed-in profile is applied by the effect below once auth resolves.
  const language = useSyncExternalStore(
    subscribeLanguage, getLanguage, getLanguage
  );

  // Firestore UI-string overrides. Reading the version here means an admin
  // edit re-renders every consumer of this context.
  const uiRevision = useSyncExternalStore(
    subscribeUiStrings, getUiStringsVersion, getUiStringsVersion
  );

  // Track whether user has manually changed language this session
  const userChangedLangRef = useRef(false);

  // Current translations object (compiled, always available offline)
  const t = getFallback(language);

  // Language metadata
  const langMeta = LANGUAGES.find(
    l => l.code === language
  ) || LANGUAGES[0];

  // ── Start the Firestore UI-string catalogue ────
  // Session-scoped singleton: hydrates from localStorage instantly, then
  // subscribes. Failure is non-fatal — the compiled T keeps the app running.
  useEffect(() => { initUiStrings(); }, []);

  // ── Apply language effects ─────────────────────
  useEffect(() => {
    const base = getBaseCode(language);
    const isRTLLang = RTL_LANGS.has(language) ||
      RTL_LANGS.has(base);

    document.documentElement.dir =
      isRTLLang ? "rtl" : "ltr";
    document.documentElement.lang = base;

    if (isRTLLang) {
      document.body.classList.add("rtl");
    } else {
      document.body.classList.remove("rtl");
    }

    applyFont(language);
    // Persist even when nothing was chosen yet, so non-React readers
    // (src/services/kisanAI.js) see the same language the UI is showing.
    writeStoredLanguage(language);
  }, [language]);

  // ── Sync from userProfile ─────────────────────
  // ONLY apply profile language on initial load.
  // Once user manually changes language this session,
  // their choice wins — profile NEVER overrides it.
  useEffect(() => {
    if (userChangedLangRef.current) return; // user changed manually → don't override
    const profileLang =
      userProfile?.preferredLanguage;
    if (profileLang &&
        isValidLang(profileLang) &&
        profileLang !== language) {
      setStoreLanguage(profileLang);
    }
  }, [userProfile?.preferredLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Change language function ───────────────────
  const changeLanguage = useCallback(
    async (newCode) => {
      const code = normalizeLang(newCode, null);
      if (!code) {
        console.warn(
          `Language ${newCode} not found`
        );
        return;
      }

      // Mark that user has explicitly chosen a language
      userChangedLangRef.current = true;

      // 1. One authoritative write. This persists to localStorage AND
      //    notifies AppContext, so t(hi,en) and the `hi` ternaries follow.
      setStoreLanguage(code);

      // 2. Save to Firestore (background)
      if (currentUser?.uid) {
        try {
          await setDoc(
            doc(db, "ki_users", currentUser.uid),
            { preferredLanguage: code },
            { merge: true }
          );
        } catch (e) {
          console.error(
            "Language save to Firestore:", e
          );
          // Non-critical — local change still works
        }
      }
    },
    [currentUser?.uid]
  );

  // ── translate() helper ─────────────────────────
  // Usage: translate("chat.placeholder")
  // Resolves through the UI-string catalogue, so Firestore overrides win
  // over the compiled T and the app still works with neither.
  const translate = useCallback((key) => {
    if (typeof key !== "string" || !key) return "";
    const dot = key.indexOf(".");
    if (dot <= 0) return key;

    const namespace = key.slice(0, dot);
    const path      = key.slice(dot + 1);
    const value     = getString(namespace, path, language);

    // getString echoes the key back when nothing matched anywhere.
    return value === path ? key : value;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, uiRevision]);

  // ── Greeting based on time of day ─────────────
  const getGreeting = useCallback((name) => {
    const hour = new Date().getHours();
    const greeting =
      hour < 12 ? t.dashboard.goodMorning :
      hour < 17 ? t.dashboard.goodAfternoon :
      t.dashboard.goodEvening;
    return name
      ? `${greeting}, ${name}!`
      : `${greeting}!`;
  }, [t]);

  // ── lt(): resolve text from a {en,hi,bn,...} map ──
  const lt = useCallback((textMap) => {
    if (!textMap) return "";
    if (typeof textMap === "string") return textMap;
    return textMap[language] ||
      textMap[getBaseCode(language)] ||
      textMap["hi-en"] ||
      textMap.hi ||
      textMap.en ||
      Object.values(textMap)[0] ||
      "";
  }, [language]);

  // ── formatNumber(): locale-aware number ──
  const formatNumber = useCallback((num, options = {}) => {
    if (num === null || num === undefined) return "--";
    const base = getBaseCode(language);
    const locale = base === "ta" ? "ta-IN" :
                   base === "te" ? "te-IN" :
                   base === "kn" ? "kn-IN" :
                   base === "ml" ? "ml-IN" :
                   "en-IN";
    try {
      return new Intl.NumberFormat(locale, options).format(num);
    } catch {
      return String(num);
    }
  }, [language]);

  // ── formatCurrency(): compact ₹ format ──
  const formatCurrency = useCallback((amount) => {
    if (!amount && amount !== 0) return "--";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)} Cr`;
    if (amount >= 100000)   return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000)     return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount}`;
  }, []);

  // ── formatDate(): locale-aware date ──
  const formatDate = useCallback((date, options = {}) => {
    if (!date) return "--";
    const d = date instanceof Date ? date : new Date(date);
    const base = getBaseCode(language);
    const locale = base === "hi" || base === "mr" ? "hi-IN" :
                   base === "bn" ? "bn-IN" :
                   base === "ta" ? "ta-IN" :
                   base === "te" ? "te-IN" :
                   "en-IN";
    try {
      return d.toLocaleDateString(locale, {
        day: "numeric", month: "short", year: "numeric", ...options
      });
    } catch {
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", ...options });
    }
  }, [language]);

  // ── getAIInstruction(): AI prompt instruction ──
  const getAIInstruction = useCallback(() =>
    AI_INSTRUCTION[language] ||
    AI_INSTRUCTION[getBaseCode(language)] ||
    AI_INSTRUCTION.en || "",
  [language]);

  const value = {
    language,
    changeLanguage,
    t,
    translate,
    lt,
    formatNumber,
    formatCurrency,
    formatDate,
    getAIInstruction,
    getGreeting,
    langMeta,
    voiceCode: VOICE_CODES[language] || "en-IN",
    isRTL: RTL_LANGS.has(language) ||
      RTL_LANGS.has(getBaseCode(language)),
    isMixed: langMeta?.type === "mixed",
    baseCode: getBaseCode(language),
    aiInstruction:
      AI_INSTRUCTION[language] ||
      AI_INSTRUCTION.en,
    availableLanguages: LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────
export function useLanguage() {
  const ctx = useContext(LanguageContext);

  // Safe fallback if used outside provider. Reads the shared store directly
  // (non-reactive) so it still reports the real language instead of "en".
  if (!ctx) {
    const language  = getLanguage();
    const fallbackT = getFallback(language);
    return {
      language,
      changeLanguage:     async (code) => { setStoreLanguage(code); },
      t:                  fallbackT,
      translate:          (key) => key,
      lt:                 (m) => (typeof m === "string" ? m : (m?.[language] ?? m?.en ?? "")),
      formatNumber:       (n) => (n === null || n === undefined ? "--" : String(n)),
      formatCurrency:     (a) => (!a && a !== 0 ? "--" : `₹${a}`),
      formatDate:         (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "--"),
      getAIInstruction:   () => AI_INSTRUCTION[language] || AI_INSTRUCTION.en || "",
      getGreeting:        (name) =>
        `Hello${name ? ", " + name : ""}!`,
      langMeta:           LANGUAGES.find(l => l.code === language) || LANGUAGES[0],
      voiceCode:          VOICE_CODES[language] || "en-IN",
      isRTL:              RTL_LANGS.has(language) ||
                          RTL_LANGS.has(getBaseCode(language)),
      isMixed:            false,
      baseCode:           getBaseCode(language),
      aiInstruction:      AI_INSTRUCTION[language] || AI_INSTRUCTION.en,
      availableLanguages: LANGUAGES
    };
  }

  return ctx;
}

export default LanguageContext;
